import { jsx, jsxs } from 'react/jsx-runtime';
import { FontManager } from '@samuelmeuli/font-manager';
import { PureComponent } from 'react';

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

var FONT_FAMILY_DEFAULT = 'Open Sans';
var OPTIONS_DEFAULTS = {
  pickerId: '',
  families: [],
  categories: [],
  scripts: ['latin'],
  variants: ['regular'],
  filter: function filter() {
    return true;
  },
  limit: 50,
  sort: 'alphabet'
};

/**
 * Return the fontId based on the provided font family
 */
function getFontId(fontFamily) {
  return fontFamily.replace(/\s+/g, '-').toLowerCase();
}
var FontPicker = /*#__PURE__*/function (_PureComponent) {
  _inheritsLoose(FontPicker, _PureComponent);
  function FontPicker(props) {
    var _this;
    _this = _PureComponent.call(this, props) || this;
    // Instance of the FontManager class used for managing, downloading and applying fonts
    _this.fontManager = void 0;
    _this.state = {
      expanded: false,
      loadingStatus: 'loading'
    };
    _this.componentDidMount = function () {
      // Generate font list
      _this.fontManager.init().then(function () {
        _this.setState({
          loadingStatus: 'finished'
        });
      })["catch"](function (err) {
        // On error: Log error message
        _this.setState({
          loadingStatus: 'error'
        });
        console.error('Error trying to fetch the list of available fonts');
        console.error(err);
      });
    };
    /**
     * After every component update, check whether the activeFontFamily prop has changed. If so,
     * call this.setActiveFontFamily with the new font
     */
    _this.componentDidUpdate = function (prevProps) {
      var _this$props = _this.props,
        activeFontFamily = _this$props.activeFontFamily,
        onChange = _this$props.onChange;
      // If active font prop has changed: Update font family in font manager and component state
      if (activeFontFamily !== prevProps.activeFontFamily) {
        _this.setActiveFontFamily(activeFontFamily);
      }
      // If onChange prop has changed: Update onChange function in font manager
      if (onChange !== prevProps.onChange) {
        _this.fontManager.setOnChange(onChange);
      }
    };
    /**
     * EventListener for closing the font picker when clicking anywhere outside it
     */
    _this.onClose = function (e) {
      var targetEl = e.target; // Clicked element
      var fontPickerEl = document.getElementById("font-picker" + _this.fontManager.selectorSuffix);
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (targetEl === fontPickerEl) {
          // Click inside font picker: Exit
          return;
        }
        if (targetEl.parentNode) {
          // Click outside font picker: Move up the DOM
          targetEl = targetEl.parentNode;
        } else {
          // DOM root is reached: Toggle picker, exit
          _this.toggleExpanded();
          return;
        }
      }
    };
    /**
     * Update the active font on font button click
     */
    _this.onSelection = function (e) {
      var target = e.target;
      var activeFontFamily = target.textContent;
      if (!activeFontFamily) {
        throw Error("Missing font family in clicked font button");
      }
      _this.setActiveFontFamily(activeFontFamily);
      _this.toggleExpanded();
    };
    /**
     * Set the specified font as the active font in the fontManager and update activeFontFamily in the
     * state
     */
    _this.setActiveFontFamily = function (activeFontFamily) {
      _this.fontManager.setActiveFont(activeFontFamily);
    };
    /**
     * Generate <ul> with all font families
     */
    _this.generateFontList = function (fonts) {
      var activeFontFamily = _this.props.activeFontFamily;
      var loadingStatus = _this.state.loadingStatus;
      if (loadingStatus !== 'finished') {
        return jsx("div", {});
      }
      return jsx("ul", {
        className: 'font-list',
        children: fonts.map(function (font) {
          var isActive = font.family === activeFontFamily;
          var fontId = getFontId(font.family);
          return jsx("li", {
            className: 'font-list-item',
            children: jsx("button", {
              type: 'button',
              id: "font-button-" + fontId + _this.fontManager.selectorSuffix,
              className: "font-button " + (isActive ? 'active-font' : ''),
              onClick: _this.onSelection,
              onKeyPress: _this.onSelection,
              children: font.family
            })
          }, fontId);
        })
      });
    };
    /**
     * Expand/collapse the picker's font list
     */
    _this.toggleExpanded = function () {
      var expanded = _this.state.expanded;
      if (expanded) {
        _this.setState({
          expanded: false
        });
        document.removeEventListener('click', _this.onClose);
      } else {
        _this.setState({
          expanded: true
        });
        document.addEventListener('click', _this.onClose);
      }
    };
    _this.render = function () {
      var _this$props2 = _this.props,
        activeFontFamily = _this$props2.activeFontFamily,
        sort = _this$props2.sort;
      var _this$state = _this.state,
        expanded = _this$state.expanded,
        loadingStatus = _this$state.loadingStatus;
      // Extract and sort font list
      var fonts = Array.from(_this.fontManager.getFonts().values());
      if (sort === 'alphabet') {
        fonts.sort(function (font1, font2) {
          return font1.family.localeCompare(font2.family);
        });
      }
      // Render font picker button and attach font list to it
      return jsxs("div", {
        id: "font-picker" + _this.fontManager.selectorSuffix,
        className: expanded ? 'expanded' : '',
        children: [jsxs("button", {
          type: 'button',
          className: 'dropdown-button',
          onClick: _this.toggleExpanded,
          onKeyPress: _this.toggleExpanded,
          children: [jsx("p", {
            className: 'dropdown-font-family',
            children: activeFontFamily
          }), jsx("p", {
            className: "dropdown-icon " + loadingStatus
          })]
        }), loadingStatus === 'finished' && _this.generateFontList(fonts)]
      });
    };
    var _this$props3 = _this.props,
      apiKey = _this$props3.apiKey,
      _activeFontFamily = _this$props3.activeFontFamily,
      pickerId = _this$props3.pickerId,
      families = _this$props3.families,
      categories = _this$props3.categories,
      scripts = _this$props3.scripts,
      variants = _this$props3.variants,
      filter = _this$props3.filter,
      limit = _this$props3.limit,
      _sort = _this$props3.sort,
      _onChange = _this$props3.onChange;
    var options = {
      pickerId: pickerId,
      families: families,
      categories: categories,
      scripts: scripts,
      variants: variants,
      filter: filter,
      limit: limit,
      sort: _sort
    };
    // Initialize FontManager object
    _this.fontManager = new FontManager(apiKey, _activeFontFamily, options, _onChange);
    return _this;
  }
  return FontPicker;
}(PureComponent);
FontPicker.defaultProps = {
  activeFontFamily: FONT_FAMILY_DEFAULT,
  onChange: function onChange() {},
  pickerId: OPTIONS_DEFAULTS.pickerId,
  families: OPTIONS_DEFAULTS.families,
  categories: OPTIONS_DEFAULTS.categories,
  scripts: OPTIONS_DEFAULTS.scripts,
  variants: OPTIONS_DEFAULTS.variants,
  filter: OPTIONS_DEFAULTS.filter,
  limit: OPTIONS_DEFAULTS.limit,
  sort: OPTIONS_DEFAULTS.sort
};

export { FontPicker as default };
//# sourceMappingURL=font-picker-react.esm.js.map
